// src/hooks/PwaInstallContext.js
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const PwaInstallContext = createContext(null);

// Utilidades plataforma (con guard SSR)
const isStandaloneDisplayMode = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
    // iOS Safari
    (typeof window.navigator !== 'undefined' && window.navigator.standalone === true)
  );
};

// Guardamos última vez que mostramos prompt para no “spamear”
const LS_KEY_LAST_PROMPT = 'pwa.lastPromptAt';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function usePwaInstallImpl() {
  const deferredRef = useRef(null);

  const [isInstalled, setIsInstalled] = useState(() => isStandaloneDisplayMode());
  const [isInstallable, setIsInstallable] = useState(false);
  const [lastPromptAt, setLastPromptAt] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const v = Number(window.localStorage.getItem(LS_KEY_LAST_PROMPT) || 0);
    return Number.isFinite(v) ? v : 0;
  });

  // Detectar “instalado”
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateInstalled = () => setIsInstalled(isStandaloneDisplayMode());
    updateInstalled();

    const onAppInstalled = () => {
      setIsInstalled(true);
      // Al instalar limpiamos el estado del prompt diferido
      deferredRef.current = null;
      setIsInstallable(false);
    };

    window.addEventListener('appinstalled', onAppInstalled);
    return () => window.removeEventListener('appinstalled', onAppInstalled);
  }, []);

  // Capturar beforeinstallprompt para poder lanzar prompt más tarde
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onBeforeInstallPrompt = (e) => {
      // Interceptamos para controlar CUÁNDO mostrarlo (banner propio)
      e.preventDefault();
      deferredRef.current = e;
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, []);

  // Condición de “puedo sugerir instalación” (instalable, no instalado, cooldown)
  const canSuggest = useMemo(() => {
    return isInstallable && !isInstalled && (Date.now() - lastPromptAt > ONE_WEEK_MS);
  }, [isInstallable, isInstalled, lastPromptAt]);

  // Mostrar prompt nativo bajo demanda
  const promptInstall = async () => {
    const ev = deferredRef.current;
    if (!ev) return { outcome: 'unavailable' };

    // Guardamos marca de tiempo para el cooldown
    const now = Date.now();
    setLastPromptAt(now);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LS_KEY_LAST_PROMPT, String(now));
    }

    // Lanzamos prompt nativo
    ev.prompt();
    try {
      const choice = await ev.userChoice;
      // Tras decidir, invalidamos el evento diferido
      deferredRef.current = null;
      setIsInstallable(false);
      return { outcome: choice?.outcome || 'unknown' }; // 'accepted' | 'dismissed'
    } catch {
      deferredRef.current = null;
      setIsInstallable(false);
      return { outcome: 'error' };
    }
  };

  // Reset manual (por si quieres permitir reintentos en menos de una semana)
  const resetInstallPrompt = () => {
    deferredRef.current = null;
    setIsInstallable(false);
  };

  return {
    // Estado
    isInstalled,         // true si se ejecuta como standalone/PWA
    isInstallable,       // true si tenemos deferPrompt listo
    canSuggest,          // true si conviene sugerir (respeta cooldown)
    deferredPromptAvailable: !!deferredRef.current,

    // Acciones
    promptInstall,
    resetInstallPrompt,

    // Utilidades
    lastPromptAt,
    setLastPromptAt: (ts) => {
      setLastPromptAt(ts);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LS_KEY_LAST_PROMPT, String(ts));
      }
    },
    cooldownMs: ONE_WEEK_MS,
  };
}

export const PwaInstallProvider = ({ children }) => {
  const pwaState = usePwaInstallImpl();
  return (
    <PwaInstallContext.Provider value={pwaState}>
      {children}
    </PwaInstallContext.Provider>
  );
};

export const usePwaInstall = () => useContext(PwaInstallContext);
